import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import SignalChip from './intelligence/SignalChip';
import { createPanelStyle, intelligencePalette } from './intelligence/intelligenceTheme';
import { logPageView } from './analytics';

const MIN_POLL_MS = 60000;

function panel(extra = {}) {
  return createPanelStyle({ padding: 20, ...extra });
}

function fmtDateTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtConfidence(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

function useSelectedSubject() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      subjectType: String(params.get('subjectType') || '').trim().toLowerCase(),
      subjectId: String(params.get('subjectId') || '').trim(),
    };
  }, [location.search]);
}

function ScopeBadge() {
  return <SignalChip label="scope: macro" tone="watch" />;
}

function StatusBadge({ status, overriddenStatus }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <SignalChip label={status || 'unknown'} tone={status} />
      {overriddenStatus ? <SignalChip label={`override: ${overriddenStatus}`} tone="weak" /> : null}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section style={panel()}>
      <div style={{ fontSize: 18, fontWeight: 850, marginBottom: 12 }}>{title}</div>
      {children}
    </section>
  );
}

function StateNotice({ title, body, tone = 'muted' }) {
  const color = tone === 'danger'
    ? intelligencePalette.danger
    : tone === 'amber'
      ? intelligencePalette.amber
      : intelligencePalette.muted;
  return (
    <div style={{ ...panel({ display: 'grid', gap: 8 }), color }}>
      <div style={{ fontSize: 16, fontWeight: 800 }}>{title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function SubjectCard({ card, selected, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        ...panel({ padding: 18, textAlign: 'left' }),
        width: '100%',
        cursor: 'pointer',
        borderColor: selected ? intelligencePalette.borderStrong : intelligencePalette.border,
        boxShadow: selected ? '0 0 0 1px rgba(77, 121, 255, 0.22), 0 18px 48px rgba(0, 0, 0, 0.25)' : '0 18px 48px rgba(0, 0, 0, 0.25)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <ScopeBadge />
            <SignalChip label={card.subjectType} tone="watch" />
            {card.freshness === 'stale' ? <SignalChip label="stale" tone="decaying" /> : null}
          </div>
          <div style={{ fontSize: 20, fontWeight: 850, color: intelligencePalette.text }}>{card.title}</div>
          <div style={{ color: intelligencePalette.muted, fontSize: 14, lineHeight: 1.5 }}>{card.summary || card.claim || 'No summary yet.'}</div>
        </div>
        <StatusBadge status={card.status} overriddenStatus={card.overriddenStatus} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <Fact label="Confidence" value={`${fmtConfidence(card.confidence)} · ${card.confidenceBand || '—'}`} />
        <Fact label="Latest delta" value={card.latestDelta || '—'} />
        <Fact label="Last evaluated" value={fmtDateTime(card.lastEvaluatedAt)} />
        <Fact label="Evidence balance" value={`${card.supportCount || 0} support / ${card.contradictionCount || 0} contradiction`} />
        <Fact label="Freshness" value={card.freshness === 'stale' ? `stale${card.staleReason ? ` · ${card.staleReason}` : ''}` : card.freshness || '—'} />
      </div>
    </button>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ border: `1px solid ${intelligencePalette.border}`, borderRadius: 14, padding: 12, background: 'rgba(255,255,255,0.02)' }}>
      <div style={{ color: intelligencePalette.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ color: intelligencePalette.text, fontSize: 14, fontWeight: 800, marginTop: 6, lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

function fmtRate(metric) {
  if (!metric) return '—';
  if (metric.rate == null) return `n=${metric.sampleSize || 0} · insufficient sample`;
  return `${Math.round(metric.rate * 100)}% · n=${metric.sampleSize}`;
}

function QualitySummary({ summary }) {
  if (!summary) return null;
  const classifications = summary.outcomeClassifications || {};
  return (
    <section style={panel()}>
      <div style={{ fontSize: 18, fontWeight: 850, marginBottom: 12, color: intelligencePalette.text }}>Intelligence Quality</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <Fact label="Accuracy" value={fmtRate(summary.accuracy)} />
        <Fact label="Pending / inconclusive" value={`${summary.pendingCount || 0} pending / ${summary.inconclusiveCount || 0} inconclusive`} />
        <Fact label="Invalidations" value={summary.invalidationCount || 0} />
        <Fact label="False positives" value={classifications.falsePositive || 0} />
        <Fact label="False negatives" value={classifications.falseNegative || 0} />
        <Fact label="Missed opportunities" value={(summary.missedOpportunities || []).length} />
        <Fact label="Blocked / abstained" value={`${summary.blockedAbstainedDistribution?.blocked || 0} blocked / ${summary.blockedAbstainedDistribution?.abstained || 0} abstained`} />
      </div>
    </section>
  );
}

function EvidenceList({ items, emptyLabel }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div style={{ color: intelligencePalette.muted, fontSize: 14 }}>{emptyLabel}</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {items.map((item) => (
        <div key={item.id || item.label || item.signalType} style={{ border: `1px solid ${intelligencePalette.border}`, borderRadius: 14, padding: 12 }}>
          <div style={{ color: intelligencePalette.text, fontWeight: 800 }}>{item.label || item.signalType || item.description}</div>
          <div style={{ color: intelligencePalette.muted, fontSize: 14, lineHeight: 1.5, marginTop: 6 }}>{item.note || item.reason || item.detail || item.description}</div>
          {item.capturedAt || item.measuredAt ? (
            <div style={{ color: intelligencePalette.muted, fontSize: 12, marginTop: 8 }}>
              {fmtDateTime(item.capturedAt || item.measuredAt)}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ThesisDetail({ detail }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DetailSection title="Claim">
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <ScopeBadge />
            <SignalChip label="thesis" tone="watch" />
            <StatusBadge status={detail.status} overriddenStatus={detail.overriddenStatus} />
          </div>
          <div style={{ color: intelligencePalette.text, fontSize: 20, fontWeight: 850 }}>{detail.title}</div>
          <div style={{ color: intelligencePalette.text, fontSize: 15, lineHeight: 1.6 }}>{detail.claim}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Fact label="Current state" value={detail.status || '—'} />
            <Fact label="Confidence" value={`${fmtConfidence(detail.confidence)} · ${detail.confidenceBand || '—'}`} />
            <Fact label="Latest delta" value={detail.latestDelta || '—'} />
            <Fact label="Last evaluated" value={fmtDateTime(detail.lastEvaluatedAt)} />
          </div>
          {detail.overrideAudit ? (
            <div style={{ border: `1px solid ${intelligencePalette.amber}`, borderRadius: 8, padding: 12, color: intelligencePalette.text, background: 'rgba(255, 193, 7, 0.08)' }}>
              <div style={{ fontWeight: 850, marginBottom: 6 }}>Human override audit</div>
              <div style={{ color: intelligencePalette.muted, fontSize: 14, lineHeight: 1.5 }}>
                {detail.overrideAudit.overriddenStatus || detail.overriddenStatus} by {detail.overrideAudit.performedBy} at {fmtDateTime(detail.overrideAudit.performedAt)} · verdict {detail.overrideAudit.verdictId}
              </div>
              <div style={{ color: intelligencePalette.text, fontSize: 14, lineHeight: 1.5, marginTop: 6 }}>{detail.overrideAudit.justification}</div>
            </div>
          ) : null}
        </div>
      </DetailSection>
      <DetailSection title="Why Confidence Changed">
        <div style={{ color: intelligencePalette.text, lineHeight: 1.6, marginBottom: 14 }}>{detail.scoreExplanation?.summary || 'No score explanation available.'}</div>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div>
            <div style={{ color: intelligencePalette.positive, fontWeight: 800, marginBottom: 8 }}>Supporting evidence</div>
            <EvidenceList items={detail.scoreExplanation?.largestPositive} emptyLabel="No supporting evidence listed." />
          </div>
          <div>
            <div style={{ color: intelligencePalette.danger, fontWeight: 800, marginBottom: 8 }}>Contradicting evidence</div>
            <EvidenceList items={detail.scoreExplanation?.largestNegative} emptyLabel="No contradicting evidence listed." />
          </div>
          <div>
            <div style={{ color: intelligencePalette.amber, fontWeight: 800, marginBottom: 8 }}>Missing or stale evidence</div>
            <EvidenceList items={[...(detail.scoreExplanation?.missing || []), ...(detail.scoreExplanation?.stale || [])]} emptyLabel="No missing or stale evidence listed." />
          </div>
        </div>
      </DetailSection>
      <DetailSection title="Expected Next">
        <EvidenceList items={detail.expectedNext} emptyLabel="No expected-next observations listed." />
      </DetailSection>
      <DetailSection title="Invalidation Predicates">
        <EvidenceList items={detail.invalidationPredicates?.map((item) => ({ ...item, description: `${item.label} · ${item.state}` }))} emptyLabel="No invalidation predicates listed." />
      </DetailSection>
      <DetailSection title="Actions">
        <EvidenceList items={detail.actions?.map((item) => ({ ...item, note: `${item.measurementWindow} · ${item.status}` }))} emptyLabel="No actions recorded yet." />
      </DetailSection>
      <DetailSection title="Outcomes">
        <EvidenceList items={detail.outcomes?.map((item) => ({ ...item, note: `${item.result} · ${fmtDateTime(item.measuredAt)}` }))} emptyLabel="No outcomes recorded yet." />
      </DetailSection>
      <DetailSection title="Verdict Timeline">
        <EvidenceList items={detail.verdictTimeline?.map((item) => ({ ...item, label: `${fmtDateTime(item.evaluatedAt)} · ${item.status} · ${fmtConfidence(item.confidence)}`, note: item.delta }))} emptyLabel="No verdict history recorded yet." />
      </DetailSection>
      <DetailSection title="Evidence Balance">
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div>
            <div style={{ color: intelligencePalette.positive, fontWeight: 800, marginBottom: 8 }}>Supporting evidence</div>
            <EvidenceList items={detail.supportingEvidence} emptyLabel="No supporting evidence recorded yet." />
          </div>
          <div>
            <div style={{ color: intelligencePalette.danger, fontWeight: 800, marginBottom: 8 }}>Contradicting evidence</div>
            <EvidenceList items={detail.contradictingEvidence} emptyLabel="No contradicting evidence recorded yet." />
          </div>
        </div>
      </DetailSection>
    </div>
  );
}

function StoryDetail({ detail, onOpenRelated }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DetailSection title="Story Detail">
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <ScopeBadge />
            <SignalChip label="story" tone="watch" />
            <StatusBadge status={detail.status} overriddenStatus={detail.overriddenStatus} />
          </div>
          <div style={{ color: intelligencePalette.text, fontSize: 20, fontWeight: 850 }}>{detail.title}</div>
          <div style={{ color: intelligencePalette.text, fontSize: 15, lineHeight: 1.6 }}>{detail.summary}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Fact label="Confidence" value={`${fmtConfidence(detail.confidence)} · ${detail.confidenceBand || '—'}`} />
            <Fact label="Latest delta" value={detail.latestDelta || '—'} />
            <Fact label="Last evaluated" value={fmtDateTime(detail.lastEvaluatedAt)} />
            <Fact label="Freshness" value={detail.freshness === 'stale' ? `stale · ${detail.staleReason}` : detail.freshness || '—'} />
          </div>
        </div>
      </DetailSection>
      <DetailSection title="Evidence Timeline">
        <EvidenceList items={detail.evidenceTimeline?.map((item) => ({ ...item, note: `${item.kind} · ${item.sourceMode} evidence` }))} emptyLabel="No evidence timeline recorded yet." />
      </DetailSection>
      <DetailSection title="Entities And Networks">
        <EvidenceList items={detail.entitiesAndNetworks?.map((item) => ({ ...item, label: `${item.chain} · ${item.entity}`, note: `${item.evidenceMode} evidence · ${item.notes}` }))} emptyLabel="No entities or networks recorded yet." />
      </DetailSection>
      <DetailSection title="Related Theses">
        {Array.isArray(detail.relatedTheses) && detail.relatedTheses.length > 0 ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {detail.relatedTheses.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpenRelated(item.id)}
                style={{ ...panel({ padding: 14, textAlign: 'left' }), cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                  <div style={{ color: intelligencePalette.text, fontWeight: 800 }}>{item.title}</div>
                  <SignalChip label={item.status} tone={item.status} />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div style={{ color: intelligencePalette.muted, fontSize: 14 }}>No related theses recorded yet.</div>
        )}
      </DetailSection>
      <DetailSection title="30/60/90-Day Changes">
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Fact label="30d" value={detail.changes?.days30 || 'Unavailable'} />
          <Fact label="60d" value={detail.changes?.days60 || 'Unavailable'} />
          <Fact label="90d" value={detail.changes?.days90 || 'Unavailable'} />
        </div>
      </DetailSection>
    </div>
  );
}

export default function MacroIntelligencePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectType, subjectId } = useSelectedSubject();
  const [indexState, setIndexState] = useState({ loading: true, error: '', data: null });
  const [detailState, setDetailState] = useState({ loading: false, error: '', data: null });

  useEffect(() => {
    logPageView('macro_intelligence_console', location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let active = true;
    let intervalId = null;

    async function loadIndex() {
      try {
        if (active) {
          setIndexState((prev) => ({ ...prev, loading: true, error: '' }));
        }
        const response = await fetch(`${SERVER_NAME}/api/macro-intelligence`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!active) return;
        setIndexState({ loading: false, error: '', data: payload });
        const pollMs = Math.max(MIN_POLL_MS, Number(payload && payload.pollFloorMs) || MIN_POLL_MS);
        if (intervalId) window.clearInterval(intervalId);
        intervalId = window.setInterval(loadIndex, pollMs);
      } catch (err) {
        if (!active) return;
        setIndexState({ loading: false, error: 'Failed to load macro intelligence.', data: null });
      }
    }

    loadIndex();

    return () => {
      active = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!subjectId || !subjectType) {
      setDetailState({ loading: false, error: '', data: null });
      return undefined;
    }

    async function loadDetail() {
      try {
        setDetailState({ loading: true, error: '', data: null });
        const response = await fetch(`${SERVER_NAME}/api/macro-intelligence/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!active) return;
        setDetailState({ loading: false, error: '', data: payload });
      } catch (err) {
        if (!active) return;
        setDetailState({ loading: false, error: 'Failed to load detail view.', data: null });
      }
    }

    loadDetail();
    return () => {
      active = false;
    };
  }, [subjectId, subjectType]);

  const cards = indexState.data && Array.isArray(indexState.data.cards) ? indexState.data.cards : [];
  const explicitSelected = cards.find((card) => card.id === subjectId && card.subjectType === subjectType);
  const defaultCard = cards[0] || null;
  const selectedCard = explicitSelected || defaultCard;

  useEffect(() => {
    if (!subjectId && defaultCard) {
      const params = new URLSearchParams(location.search);
      params.set('subjectType', defaultCard.subjectType);
      params.set('subjectId', defaultCard.id);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [defaultCard, location.pathname, location.search, navigate, subjectId]);

  function openSubject(nextType, nextId) {
    const params = new URLSearchParams(location.search);
    params.set('subjectType', nextType);
    params.set('subjectId', nextId);
    navigate(`${location.pathname}?${params.toString()}`);
  }

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, rgba(77,121,255,0.16), transparent 42%), linear-gradient(180deg, #060b18 0%, #091224 58%, #04070f 100%)' }}>
      <div style={{ maxWidth: 1340, margin: '0 auto', padding: '32px 20px 48px', display: 'grid', gap: 20 }}>
        <section style={panel({ padding: 24, background: 'linear-gradient(135deg, rgba(18, 28, 56, 0.98), rgba(7, 11, 24, 0.96))' })}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <ScopeBadge />
            <SignalChip label="macro intelligence" tone="watch" />
            <SignalChip label="separate from token console" tone="weak" />
          </div>
          <div style={{ color: intelligencePalette.text, fontSize: 32, lineHeight: 1.1, fontWeight: 900, marginBottom: 10 }}>
            Macro Intelligence Console
          </div>
          <div style={{ color: intelligencePalette.muted, fontSize: 15, lineHeight: 1.6, maxWidth: 920 }}>
            Story and Thesis monitoring for macro-scoped regime questions. This page is intentionally separate from the token-scoped `/thesis`, `/stories`, and `/agent-verdicts` surfaces.
          </div>
          <div style={{ color: intelligencePalette.muted, fontSize: 12, marginTop: 12 }}>
            Polling floor: {Math.max(MIN_POLL_MS, Number(indexState.data?.pollFloorMs) || MIN_POLL_MS) / 1000}s
          </div>
        </section>
        <QualitySummary summary={indexState.data?.qualitySummary} />

        {indexState.loading ? <StateNotice title="Loading" body="Loading macro story and thesis cards." /> : null}
        {indexState.error ? <StateNotice title="Error" body={indexState.error} tone="danger" /> : null}
        {!indexState.loading && !indexState.error && cards.length === 0 ? (
          <StateNotice title="Empty" body="No macro stories or theses are available yet." />
        ) : null}

        {!indexState.loading && !indexState.error && cards.length > 0 ? (
          <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(0, 420px) minmax(0, 1fr)' }}>
            <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
              {cards.map((card) => (
                <SubjectCard
                  key={card.id}
                  card={card}
                  selected={selectedCard && selectedCard.id === card.id && selectedCard.subjectType === card.subjectType}
                  onOpen={() => openSubject(card.subjectType, card.id)}
                />
              ))}
            </div>
            <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
              {selectedCard && selectedCard.freshness === 'stale' ? (
                <StateNotice title="Stale" body={selectedCard.staleReason || 'This macro subject contains stale evidence and should be reviewed cautiously.'} tone="amber" />
              ) : null}
              {selectedCard && selectedCard.status === 'insufficient_evidence' ? (
                <StateNotice title="Insufficient Evidence" body="This subject is being tracked, but the current evidence set is not strong enough to support a directional conclusion." tone="amber" />
              ) : null}
              {detailState.loading ? <StateNotice title="Loading Detail" body="Loading macro detail view." /> : null}
              {detailState.error ? <StateNotice title="Error" body={detailState.error} tone="danger" /> : null}
              {!detailState.loading && !detailState.error && detailState.data ? (
                detailState.data.subjectType === 'story' ? (
                  <StoryDetail detail={detailState.data} onOpenRelated={(id) => openSubject('thesis', id)} />
                ) : (
                  <ThesisDetail detail={detailState.data} />
                )
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
